import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from './Modal';
import { useTagSearch } from '../contexts/TagSearchContext';
import { useContent } from '../contexts/ContentContext';
import { useTooltip } from '../contexts/TooltipContext';
import { getConfig } from '../config';
import { fetchPublicSearch, getIndex, getYearIndex, MonthData, YearData } from '../services/api';
import { InitialIndex, InitialWordItem, RootIndex, WordCategoryItem } from '../types/word';
import { getFrequentTags, getRecentTags, recordTagUse } from '../utils/tagHistory';
import { searchFilters, searchConditions } from '../utils/publicSearch';
import { imageOrigin, illustrationHome, keepIllustrationRoot } from '../utils/illustrationRoute';
import AttributePicker from './AttributePicker';
import SearchConditionChips from './SearchConditionChips';

type SearchTab = 'date' | 'word' | 'color' | 'attributes';
const SEARCH_TABS: SearchTab[] = ['attributes', 'date', 'word', 'color'];

const POPULAR_LIMIT = 12;
const RECENT_LIMIT = 8;
const WORDS_PER_PAGE = 30;
const isBrowsableTag = (value: string) => !/^[a-z0-9_-]+$/i.test(value);

interface ColorIndexItem {
    key: string;
    label?: string;
    hex?: string;
    count: number;
}

const TagSearchModal: React.FC = () => {
    const { isOpen, closeTagSearch, relatedTags } = useTagSearch();
    const { content, language, t } = useContent();
    const { showTooltip, hideTooltip } = useTooltip();
    const { apiBaseUrl } = getConfig();

    const [years, setYears] = useState<YearData[]>([]);
    const [monthsByYear, setMonthsByYear] = useState<Record<string, MonthData[]>>({});
    const [expandedYear, setExpandedYear] = useState<string | null>(null);
    const [loadingYears, setLoadingYears] = useState(false);
    const [categories, setCategories] = useState<WordCategoryItem[]>([]);
    const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
    const [words, setWords] = useState<InitialWordItem[]>([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [loadingWords, setLoadingWords] = useState(false);
    const [loadingAllWords, setLoadingAllWords] = useState(false);
    const [wordPage, setWordPage] = useState(1);
    const [wordTotalPages, setWordTotalPages] = useState(0);
    const [search, setSearch] = useState('');
    const [error, setError] = useState<string | null>(null);
    const cacheRef = useRef<Map<string, { words: InitialWordItem[]; pagination?: InitialIndex['pagination'] }>>(new Map());
    const allWordsCacheRef = useRef<Map<string, InitialWordItem[]>>(new Map());
    const [allWords, setAllWords] = useState<InitialWordItem[]>([]);
    const [recentTags, setRecentTags] = useState<string[]>([]);
    const [frequentTags, setFrequentTags] = useState<string[]>([]);
    const [popularTags, setPopularTags] = useState<InitialWordItem[]>([]);
    const [loadingPopular, setLoadingPopular] = useState(false);
    const [colors, setColors] = useState<ColorIndexItem[]>([]);
    const [loadingColors, setLoadingColors] = useState(false);
    const [colorSearch, setColorSearch] = useState('');
    const [activeTab, setActiveTab] = useState<SearchTab>('date');
    const [draft, setDraft] = useState(() => new URLSearchParams());
    const sourceParams = useRef(new URLSearchParams());
    const stock = useRef<HTMLDivElement>(null);
    const flights = useRef(new Set<HTMLElement>());
    const [count, setCount] = useState<number | null>(null);
    const [countStatus, setCountStatus] = useState<'loading' | 'ready' | 'error'>('loading');
    const [countQuery, setCountQuery] = useState('');
    const [countAttempt, setCountAttempt] = useState(0);
    const draftQuery = searchFilters(draft).toString();
    const currentCountStatus = countQuery === draftQuery ? countStatus : 'loading';
    const tabLabels = [t('search.attributes'), content.ui[language].tagSearch.dateTab,
        content.ui[language].tagSearch.wordTab, t('searchTagSearchModal.text1')];

    useEffect(() => {
        setCount(null); setCountStatus('loading'); setCountQuery(draftQuery);
        if (!isOpen || !draftQuery) return;
        const controller = new AbortController();
        const timer = window.setTimeout(() => {
            const query = new URLSearchParams(draftQuery);
            query.set('per_page', '1');
            fetchPublicSearch(query, controller.signal).then(result => {
                if (!controller.signal.aborted) { setCount(result.pagination.total_items); setCountStatus('ready'); }
            }).catch(() => { if (!controller.signal.aborted) setCountStatus('error'); });
        }, 200);
        return () => { window.clearTimeout(timer); controller.abort(); };
    }, [isOpen, draftQuery, countAttempt]);

    useEffect(() => {
        if (!isOpen) return;
        if (isOpen) {
            const route = new URLSearchParams(window.location.search);
            const origin = route.get('view') === 'image' ? imageOrigin(route) : null;
            const params = new URLSearchParams(origin ? origin.slice(2).split('#')[0] : window.location.search);
            if (route.get('view') === 'image' && !origin) { params.delete('year'); params.delete('month'); }
            sourceParams.current = params;
            setDraft(searchFilters(params));
            const tab = params.get('searchTab') as SearchTab;
            const page = params.get('page');
            if (SEARCH_TABS.includes(tab)) setActiveTab(tab);
            else if (page === 'search' || page === 'illustrations' || page === 'favorites') setActiveTab('attributes');
            else if (page === 'workflow' || page === 'ratings' || ['workflow', 'ratings'].includes(tab)) setActiveTab('attributes');
            else if (page === 'word' || page === 'color') setActiveTab(page);
            else setActiveTab('date');
        }
        const elements = flights.current;
        return () => { elements.forEach(element => element.remove()); elements.clear(); };
    }, [isOpen]);

    const flyToStock = (source?: HTMLElement, text?: string) => {
        if (!source || !stock.current || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        const from = source.getBoundingClientRect(), to = stock.current.getBoundingClientRect();
        const element = document.createElement('span');
        element.className = 'lab-condition-flight'; element.setAttribute('aria-hidden', 'true');
        element.textContent = text || source.textContent || '';
        Object.assign(element.style, { left: `${from.left}px`, top: `${from.top}px`, maxWidth: `${Math.min(from.width, 250)}px` });
        document.body.appendChild(element); flights.current.add(element);
        const animation = element.animate([{ transform: 'translate(0, 0) scale(1)', opacity: .9 },
            { transform: `translate(${to.left - from.left + 12}px, ${to.top - from.top + 12}px) scale(.55)`, opacity: 0 }], { duration: 450, easing: 'cubic-bezier(.2,.7,.2,1)' });
        animation.finished.catch(() => {}).finally(() => { element.remove(); flights.current.delete(element); });
    };
    const updateCondition = (key: string, value: string, source?: HTMLElement) => {
        const next = new URLSearchParams(draft);
        if (value) next.set(key, value); else next.delete(key);
        setDraft(next);
        if (value && value !== draft.get(key)) flyToStock(source);
    };
    const handleApply = () => {
        if (currentCountStatus !== 'ready' || count === null) return;
        const next = searchFilters(draft);
        keepIllustrationRoot(next, sourceParams.current);
        next.set('page', 'search');
        (draft.get('tags') || '').split(',').filter(Boolean).forEach(value => recordTagUse(value, RECENT_LIMIT));
        closeTagSearch();
        window.history.pushState({}, '', searchConditions(draft).length ? '/?' + next : illustrationHome);
        window.dispatchEvent(new PopStateEvent('popstate'));
    };

    const isSearching = Boolean(search.trim());
    const isLoadingWords = loadingWords || loadingAllWords;

    const currentColor = draft.get('color') || '';
    const isImageView = new URLSearchParams(window.location.search).get('view') === 'image';

    useEffect(() => {
        if (!isOpen) return;
        setSearch('');
        setError(null);
        setExpandedCategory(null);
        setWords([]);
        setWordPage(1);
        setWordTotalPages(0);
        setColorSearch('');

        const fetchCategories = async () => {
            setLoadingCategories(true);
            try {
                const response = await fetch(`${apiBaseUrl}/index/words/top`);
                if (!response.ok) {
                    throw new Error('Failed to load categories');
                }
                const data: RootIndex = await response.json();
                const items = (data.categories || []).filter(category => category.key !== 'other');
                setCategories(items);
                setExpandedCategory(items[0]?.key ?? null);
            } catch (err) {
                console.error('Failed to load word index', err);
                setError(content.ui[language].tagSearch.suggestionError);
            } finally {
                setLoadingCategories(false);
            }
        };

        const fetchYears = async () => {
            setLoadingYears(true);
            try {
                const rootIndex = await getIndex();
                const items = rootIndex.years || [];
                setYears(items);
                const currentYear = new URLSearchParams(window.location.search).get('year');
                setExpandedYear(currentYear || (items[0]?.year ?? null));
            } catch (err) {
                console.error('Failed to load years', err);
            } finally {
                setLoadingYears(false);
            }
        };

        const fetchColors = async () => {
            setLoadingColors(true);
            try {
                const response = await fetch(`${apiBaseUrl}/index/colors`);
                if (!response.ok) {
                    throw new Error('Failed to load colors');
                }
                const data = await response.json();
                setColors(Array.isArray(data.colors) ? data.colors : []);
            } catch (err) {
                console.error('Failed to load colors', err);
                setColors([]);
            } finally {
                setLoadingColors(false);
            }
        };

        fetchCategories();
        fetchYears();
        fetchColors();
    }, [isOpen, apiBaseUrl, content.ui, language]);

    useEffect(() => {
        if (!isOpen) return;
        setRecentTags(getRecentTags(RECENT_LIMIT).filter(isBrowsableTag));
        setFrequentTags(getFrequentTags(RECENT_LIMIT).filter(isBrowsableTag));
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        setLoadingPopular(true);
        fetch(`${apiBaseUrl}/index/words/top?top=${POPULAR_LIMIT}`)
            .then(res => res.json())
            .then((data) => {
                const words = Array.isArray(data.words) ? data.words.filter((item: InitialWordItem) => isBrowsableTag(item.word)) : [];
                setPopularTags(words);
            })
            .catch(err => {
                console.error('Failed to load popular tags', err);
                setPopularTags([]);
            })
            .finally(() => setLoadingPopular(false));
    }, [isOpen, apiBaseUrl]);

    useEffect(() => {
        if (!isOpen || !expandedYear || monthsByYear[expandedYear]) return;
        getYearIndex(expandedYear).then(yearIndex => {
            setMonthsByYear(current => ({ ...current, [expandedYear]: yearIndex.months || [] }));
        }).catch(error => {
            console.error(`Failed to fetch index for year ${expandedYear}:`, error);
        });
    }, [isOpen, expandedYear, monthsByYear]);

    const fetchWordsForCategory = useCallback(async (category: string, pageNumber: number) => {
        setLoadingWords(true);
        setError(null);
        const cacheKey = `${category}:${pageNumber}`;

        if (cacheRef.current.has(cacheKey)) {
            const cached = cacheRef.current.get(cacheKey);
            setWords(cached?.words || []);
            const pagination = cached?.pagination;
            setWordTotalPages(pagination?.total_pages ?? 0);
            setLoadingWords(false);
            return;
        }

        try {
            const response = await fetch(`${apiBaseUrl}/index/words/category/${category}.json?page=${pageNumber}&per_page=${WORDS_PER_PAGE}`);
            if (!response.ok) {
                throw new Error('Failed to load words');
            }
            const data: InitialIndex = await response.json();
            const sortedWords = (data.words || []).sort((a, b) => a.word.localeCompare(b.word));
            cacheRef.current.set(cacheKey, { words: sortedWords, pagination: data.pagination });
            setWords(sortedWords);
            setWordTotalPages(data.pagination?.total_pages ?? 0);
        } catch (err) {
            console.error('Failed to load words', err);
            setError(content.ui[language].tagSearch.suggestionError);
            setWords([]);
            setWordTotalPages(0);
        } finally {
            setLoadingWords(false);
        }
    }, [apiBaseUrl, content.ui, language]);

    useEffect(() => {
        if (!isOpen || !expandedCategory) return;
        fetchWordsForCategory(expandedCategory, wordPage);
    }, [isOpen, expandedCategory, wordPage, fetchWordsForCategory]);

    const handleCategoryClick = (category: string) => {
        if (expandedCategory === category) return;
        setExpandedCategory(category);
        setSearch('');
        setWordPage(1);
    };

    const fetchAllWordsForCategory = useCallback(async (category: string) => {
        if (allWordsCacheRef.current.has(category)) {
            setAllWords(allWordsCacheRef.current.get(category) || []);
            return;
        }

        setLoadingAllWords(true);
        setError(null);
        try {
            const response = await fetch(`${apiBaseUrl}/index/words/category/${category}.json?page=1&per_page=${WORDS_PER_PAGE}`);
            if (!response.ok) {
                throw new Error('Failed to load words');
            }
            const data: InitialIndex = await response.json();
            const totalPages = data.pagination?.total_pages ?? 1;
            const pagesToFetch = [];
            for (let page = 2; page <= totalPages; page += 1) {
                pagesToFetch.push(fetch(`${apiBaseUrl}/index/words/category/${category}.json?page=${page}&per_page=${WORDS_PER_PAGE}`));
            }

            let combinedWords = data.words || [];
            if (pagesToFetch.length) {
                const pageResponses = await Promise.all(pagesToFetch);
                const pageData = await Promise.all(pageResponses.map(res => {
                    if (!res.ok) {
                        throw new Error('Failed to load words');
                    }
                    return res.json() as Promise<InitialIndex>;
                }));
                pageData.forEach(page => {
                    combinedWords = combinedWords.concat(page.words || []);
                });
            }

            const sortedWords = combinedWords.sort((a, b) => a.word.localeCompare(b.word));
            allWordsCacheRef.current.set(category, sortedWords);
            setAllWords(sortedWords);
        } catch (err) {
            console.error('Failed to load words', err);
            setError(content.ui[language].tagSearch.suggestionError);
            setAllWords([]);
        } finally {
            setLoadingAllWords(false);
        }
    }, [apiBaseUrl, content.ui, language]);

    useEffect(() => {
        if (!isOpen || !expandedCategory) {
            setAllWords([]);
            return;
        }

        if (search.trim()) {
            fetchAllWordsForCategory(expandedCategory);
        } else {
            setAllWords([]);
        }
    }, [isOpen, expandedCategory, search, fetchAllWordsForCategory]);

    const filteredWords = useMemo(() => {
        if (!search.trim()) return words;
        const query = search.trim().toLowerCase();
        const source = isSearching ? allWords : words;
        return source.filter(w => w.word.toLowerCase().includes(query));
    }, [search, words, allWords, isSearching]);
    const browsableRelatedTags = useMemo(() => relatedTags.filter(isBrowsableTag), [relatedTags]);

    const filteredColors = useMemo(() => {
        const query = colorSearch.trim().toLowerCase();
        if (!query) return colors;
        const hexQuery = query.startsWith('#') ? query : `#${query}`;
        return colors.filter(color => {
            const key = color.key.toLowerCase();
            const label = (color.label || '').toLowerCase();
            const hex = (color.hex || '').toLowerCase();
            return key.includes(query) || label.includes(query) || hex.includes(hexQuery) || hex.includes(query);
        });
    }, [colors, colorSearch]);

    const handleSelectWord = (value: string, source?: HTMLElement) => {
        const selected = (draft.get('tags') || '').split(',').filter(Boolean);
        const exists = selected.includes(value);
        if (!exists && selected.length >= 40) { setError(t('search.tagLimit')); return; }
        const tags = exists ? selected.filter(tag => tag !== value) : [...selected, value];
        updateCondition('tags', tags.join(','));
        if (!exists) flyToStock(source, value);
    };

    const handleSelectColor = (value: string, source?: HTMLElement) => {
        updateCondition('color', currentColor === value ? '' : value, source);
    };

    const handleSelectMonth = (year: string, month: string, source?: HTMLElement) => {
        const next = new URLSearchParams(draft);
        if (next.get('year') === year && next.get('month') === month) { next.delete('year'); next.delete('month'); }
        else { next.set('year', year); next.set('month', month); flyToStock(source, `${year}/${month}`); }
        setDraft(next);
    };

    const handleClear = () => {
        setDraft(new URLSearchParams());
    };

    const renderCategoryButton = (category: WordCategoryItem) => {
        const isActive = expandedCategory === category.key;
        const displayLabel = language === 'jp' ? category.label : (category.label_en || category.label);

        return (
            <button
                key={category.key}
                type="button"
                onClick={() => handleCategoryClick(category.key)}
                className={`w-full rounded-xl border px-3 py-2 text-left text-sm font-semibold transition-colors ${isActive
                    ? 'bg-gray-900 text-white border-gray-900 dark:bg-amber-500 dark:text-gray-900 dark:border-amber-500 ivory:bg-coffee-600 ivory:text-coffee-50 ivory:border-coffee-600'
                    : 'bg-white dark:bg-gray-900 ivory:bg-coffee-50 text-gray-700 dark:text-gray-200 ivory:text-coffee-700 border-gray-200 dark:border-gray-700 ivory:border-coffee-200 hover:bg-blue-50 dark:hover:bg-blue-900/40 ivory:hover:bg-coffee-200'
                    }`}
                onMouseEnter={(e) => showTooltip(content.tooltips[language].tagSearch.word, e)}
                onMouseLeave={hideTooltip}
                title={`${displayLabel} (${category.count})`}
            >
                <span className="flex items-center justify-between gap-2">
                    <span>{displayLabel}</span>
                    <span className="text-xs opacity-70">({category.count})</span>
                </span>
            </button>
        );
    };

    return (
        <Modal helpSection="search-dialog"
            restoreFocusSelector=".lab-subheader-search button"
            isOpen={isOpen}
            onClose={closeTagSearch}
            title={content.ui[language].tagSearch.title}
            panelClassName="lab-search-dialog max-w-5xl"
            manageFocus
        >
            <div className="lab-search-dialog-body p-3 sm:p-4 bg-gradient-to-b from-white via-white to-gray-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-950 ivory:from-coffee-50 ivory:via-coffee-50 ivory:to-coffee-100">
                <div role="tablist" aria-label={content.ui[language].tagSearch.title} className="grid grid-cols-2 sm:flex items-center gap-1.5 rounded-2xl border border-gray-200 dark:border-gray-700 ivory:border-coffee-200 bg-white/90 dark:bg-gray-900/70 ivory:bg-coffee-100/80 p-1 shadow-sm">
                    {SEARCH_TABS.map((tab, index) => <button key={tab} type="button" role="tab" id={`image-search-${tab}`} aria-label={tabLabels[index]} aria-selected={activeTab === tab} aria-controls="image-search-panel"
                        tabIndex={activeTab === tab ? 0 : -1}
                        onClick={() => setActiveTab(tab)}
                        onKeyDown={event => {
                            const offset = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
                            if (!offset && event.key !== 'Home' && event.key !== 'End') return;
                            event.preventDefault();
                            const next = event.key === 'Home' ? 0 : event.key === 'End' ? SEARCH_TABS.length - 1 : (index + offset + SEARCH_TABS.length) % SEARCH_TABS.length;
                            setActiveTab(SEARCH_TABS[next]);
                            document.getElementById(`image-search-${SEARCH_TABS[next]}`)?.focus();
                        }}
                        className={`min-w-0 flex-1 px-2 py-2 rounded-xl text-sm font-semibold transition-colors ${activeTab === tab ? 'bg-gray-900 text-white dark:bg-amber-500 dark:text-gray-900 ivory:bg-coffee-600 ivory:text-coffee-50 shadow-sm' : 'text-gray-600 dark:text-gray-300 ivory:text-coffee-600 hover:bg-gray-100 dark:hover:bg-gray-800 ivory:hover:bg-coffee-200'}`}>
                        <small className="block font-normal opacity-80">{index < 4 ? t('search.conditions') : t('search.reference')}</small>
                        <span>{tabLabels[index]}</span>
                    </button>)}
                </div>
                <div ref={stock} className="lab-search-stock lab-filter-chips" aria-label={t('search.applied')} aria-live="polite">
                    <SearchConditionChips params={draft} onChange={setDraft} />
                    {!searchConditions(draft).length && <span>{t('search.empty')}</span>}
                </div>
                <div role="tabpanel" id="image-search-panel" aria-labelledby={`image-search-${activeTab}`}>
                {activeTab === 'attributes' ? <AttributePicker params={draft} onChange={updateCondition} /> : <>
                {activeTab === 'date' ? (
                    <div className="tag-search-assemble rounded-3xl border border-gray-200 dark:border-gray-700 ivory:border-coffee-200 bg-white/90 dark:bg-gray-900/70 ivory:bg-coffee-50 p-3 shadow-sm space-y-2.5">
                        <div className="tag-search-assemble-item flex items-center justify-between" style={{ '--assemble-delay': '0ms' } as React.CSSProperties}>
                            <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 ivory:text-coffee-700">
                                {content.ui[language].tagSearch.dateTitle}
                            </div>
                            {loadingYears && (
                                <span className="text-xs text-gray-500 dark:text-gray-400 ivory:text-coffee-500">{content.ui[language].tagSearch.loading}</span>
                            )}
                        </div>
                        <div className="tag-search-assemble-item flex flex-wrap gap-1.5" style={{ '--assemble-delay': '140ms' } as React.CSSProperties}>
                            {years.map(yearItem => (
                                <button
                                    key={yearItem.year}
                                    type="button"
                                    onClick={() => setExpandedYear(yearItem.year)}
                                    className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${expandedYear === yearItem.year
                                        ? 'bg-amber-500 text-white border-amber-500 dark:bg-amber-400 dark:text-gray-900 ivory:bg-coffee-600 ivory:text-coffee-50 ivory:border-coffee-600'
                                        : 'bg-white dark:bg-gray-900 ivory:bg-coffee-50 text-gray-700 dark:text-gray-200 ivory:text-coffee-700 border-gray-200 dark:border-gray-700 ivory:border-coffee-200 hover:bg-amber-50 dark:hover:bg-gray-800 ivory:hover:bg-coffee-100'
                                        }`}
                                >
                                    {yearItem.year}
                                </button>
                            ))}
                        </div>
                        <div className="tag-search-assemble-item rounded-2xl border border-gray-200 dark:border-gray-700 ivory:border-coffee-200 bg-gray-50/80 dark:bg-gray-800/70 ivory:bg-coffee-100 p-3 max-h-56 overflow-y-auto shadow-inner" style={{ '--assemble-delay': '280ms' } as React.CSSProperties}>
                            {expandedYear && (monthsByYear[expandedYear] || []).length === 0 && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 ivory:text-coffee-500">{content.ui[language].tagSearch.noSuggestions}</div>
                            )}
                            <div className="grid grid-cols-3 gap-2">
                                {(expandedYear ? monthsByYear[expandedYear] || [] : []).map(month => (
                                    <button
                                        key={month.month}
                                        type="button"
                                        aria-pressed={draft.get('year') === expandedYear && draft.get('month') === month.month}
                                        onClick={event => {
                                            if (expandedYear) {
                                                handleSelectMonth(expandedYear, month.month, event.currentTarget);
                                            }
                                        }}
                                        className="px-3 py-2 rounded-xl bg-white dark:bg-gray-900 ivory:bg-coffee-50 border border-gray-200 dark:border-gray-700 ivory:border-coffee-200 text-sm font-semibold text-gray-700 dark:text-gray-200 ivory:text-coffee-700 hover:bg-amber-50 dark:hover:bg-amber-900/40 ivory:hover:bg-coffee-200 transition-colors"
                                    >
                                        {month.month}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : activeTab === 'color' ? (
                    <div className="tag-search-assemble rounded-3xl border border-gray-200 dark:border-gray-700 ivory:border-coffee-200 bg-white/90 dark:bg-gray-900/70 ivory:bg-coffee-50 p-3 shadow-sm space-y-2.5">
                        <div className="tag-search-assemble-item flex items-center justify-between" style={{ '--assemble-delay': '0ms' } as React.CSSProperties}>
                            <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 ivory:text-coffee-700">
                                {t('searchTagSearchModal.text5')}
                            </div>
                            {loadingColors && (
                                <span className="text-xs text-gray-500 dark:text-gray-400 ivory:text-coffee-500">{content.ui[language].tagSearch.loading}</span>
                            )}
                        </div>
                        <div className="tag-search-assemble-item flex items-center w-full rounded-xl border border-gray-200 dark:border-gray-700 ivory:border-coffee-200 bg-white dark:bg-gray-900 ivory:bg-coffee-50 px-2.5 py-1.5 text-sm text-gray-800 dark:text-gray-100 ivory:text-coffee-800 focus-within:ring-2 focus-within:ring-amber-200 dark:focus-within:ring-amber-500" style={{ '--assemble-delay': '100ms' } as React.CSSProperties}>
                            <input
                                value={colorSearch}
                                onChange={(e) => setColorSearch(e.target.value)}
                                placeholder={t('searchTagSearchModal.text6')}
                                className="w-full bg-transparent focus:outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500 ivory:placeholder:text-coffee-400"
                            />
                        </div>
                        {!loadingColors && colors.length === 0 && (
                            <div className="tag-search-assemble-item text-xs text-gray-500 dark:text-gray-400 ivory:text-coffee-500" style={{ '--assemble-delay': '140ms' } as React.CSSProperties}>
                                {content.ui[language].tagSearch.noSuggestions}
                            </div>
                        )}
                        {!loadingColors && colors.length > 0 && filteredColors.length === 0 && (
                            <div className="tag-search-assemble-item text-xs text-gray-500 dark:text-gray-400 ivory:text-coffee-500" style={{ '--assemble-delay': '140ms' } as React.CSSProperties}>
                                {content.ui[language].tagSearch.noSuggestions}
                            </div>
                        )}
                        <div className="tag-search-assemble-item grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-80 overflow-y-auto" style={{ '--assemble-delay': '140ms' } as React.CSSProperties}>
                            {filteredColors.map(color => {
                                const isActive = currentColor === color.key;
                                return (
                                    <button
                                        key={color.key}
                                        type="button"
                                        aria-pressed={isActive}
                                        onClick={event => handleSelectColor(color.key, event.currentTarget)}
                                        className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-left text-sm font-semibold transition-colors ${isActive
                                            ? 'border-gray-900 bg-gray-900 text-white dark:border-amber-500 dark:bg-amber-500 dark:text-gray-900 ivory:border-coffee-600 ivory:bg-coffee-600 ivory:text-coffee-50'
                                            : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800 ivory:border-coffee-200 ivory:bg-coffee-50 ivory:text-coffee-700 ivory:hover:bg-coffee-100'
                                            }`}
                                        title={`${color.label || color.key} (${color.count})`}
                                    >
                                        <span
                                            className="h-5 w-5 shrink-0 rounded border border-white/70 shadow"
                                            style={{ backgroundColor: color.hex || '#000000' }}
                                        />
                                        <span className="min-w-0 flex-1 truncate">{color.label || color.key}</span>
                                        <span className="text-xs opacity-70">({color.count})</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
                            <div className="tag-search-assemble rounded-3xl border border-gray-200 dark:border-gray-700 ivory:border-coffee-200 bg-white/90 dark:bg-gray-900/70 ivory:bg-coffee-50 p-3 shadow-sm space-y-2">
                                <div className="tag-search-assemble-item text-sm font-semibold text-gray-700 dark:text-gray-200 ivory:text-coffee-700" style={{ '--assemble-delay': '0ms' } as React.CSSProperties}>
                                    {content.ui[language].tagSearch.groupLabel}
                                </div>
                                {loadingCategories && (
                                    <div className="tag-search-assemble-item text-xs text-gray-500 dark:text-gray-400 ivory:text-coffee-500" style={{ '--assemble-delay': '140ms' } as React.CSSProperties}>
                                        {content.ui[language].tagSearch.loading}
                                    </div>
                                )}
                                {!loadingCategories && (
                                    <div className="tag-search-assemble-item grid grid-cols-1 gap-1.5" style={{ '--assemble-delay': '140ms' } as React.CSSProperties}>
                                        {categories.map(category => renderCategoryButton(category))}
                                        {categories.length === 0 && (
                                            <div className="text-xs text-gray-500 dark:text-gray-400 ivory:text-coffee-500">{content.ui[language].tagSearch.noSuggestions}</div>
                                        )}
                                    </div>
                                )}
                                {error && (
                                    <div className="tag-search-assemble-item text-xs text-red-500" style={{ '--assemble-delay': '280ms' } as React.CSSProperties}>{error}</div>
                                )}
                            </div>

                            <div className="tag-search-assemble rounded-3xl border border-gray-200 dark:border-gray-700 ivory:border-coffee-200 bg-white/90 dark:bg-gray-900/70 ivory:bg-coffee-50 p-3 shadow-sm space-y-2.5">
                                <div className="tag-search-assemble-item flex items-center justify-between gap-1.5" style={{ '--assemble-delay': '0ms' } as React.CSSProperties}>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200 ivory:text-coffee-700">
                                        {content.ui[language].tagSearch.inputLabel}
                                    </label>
                                    {isLoadingWords && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400 ivory:text-coffee-500">{content.ui[language].tagSearch.loading}</span>
                                    )}
                                </div>
                                <div
                                    className="tag-search-assemble-item flex items-center w-full rounded-xl border border-gray-200 dark:border-gray-700 ivory:border-coffee-200 bg-white dark:bg-gray-900 ivory:bg-coffee-50 px-2.5 py-1.5 text-sm text-gray-800 dark:text-gray-100 ivory:text-coffee-800 focus-within:ring-2 focus-within:ring-amber-200 dark:focus-within:ring-amber-500"
                                    style={{ '--assemble-delay': '140ms' } as React.CSSProperties}
                                >
                                    <span className="mr-2 rounded-md bg-gray-100 dark:bg-gray-800 ivory:bg-coffee-100 px-2 py-0.5 text-xs font-semibold text-gray-600 dark:text-gray-300 ivory:text-coffee-600">
                                        {(() => {
                                            const category = categories.find(item => item.key === expandedCategory);
                                            return category ? (language === 'jp' ? category.label : (category.label_en || category.label)) : '-';
                                        })()}
                                    </span>
                                    <input
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder={content.ui[language].tagSearch.placeholder}
                                        className="w-full bg-transparent focus:outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500 ivory:placeholder:text-coffee-400"
                                    />
                                </div>
                                <div className="tag-search-assemble-item rounded-2xl border border-gray-200 dark:border-gray-700 ivory:border-coffee-200 bg-gray-50/80 dark:bg-gray-900/70 ivory:bg-coffee-100 p-3 max-h-48 overflow-y-auto" style={{ '--assemble-delay': '280ms' } as React.CSSProperties}>
                                    {!expandedCategory && !isLoadingWords ? (
                                        <div className="text-xs text-gray-500 dark:text-gray-400 ivory:text-coffee-500">{content.ui[language].tagSearch.helperText}</div>
                                    ) : filteredWords.length === 0 && !isLoadingWords ? (
                                        <div className="text-xs text-gray-500 dark:text-gray-400 ivory:text-coffee-500">{content.ui[language].tagSearch.noSuggestions}</div>
                                    ) : (
                                        <div className="flex flex-wrap gap-2">
                                            {filteredWords.map(item => (
                                                <button
                                                    key={item.word}
                                                    type="button"
                                                aria-pressed={(draft.get('tags') || '').split(',').includes(item.word)}
                                                onClick={event => handleSelectWord(item.word, event.currentTarget)}
                                                    className="px-2 py-0.5 rounded-full bg-white dark:bg-gray-800 ivory:bg-coffee-50 border border-gray-200 dark:border-gray-600 ivory:border-coffee-200 text-[0.65rem] text-gray-700 dark:text-gray-200 ivory:text-coffee-700 hover:bg-blue-50 dark:hover:bg-blue-900/40 ivory:hover:bg-coffee-200 transition-colors"
                                                    onMouseEnter={(e) => showTooltip(content.tooltips[language].tagSearch.word, e)}
                                                    onMouseLeave={hideTooltip}
                                                >
                                                    {item.word}
                                                    <span className="ml-1 text-xs text-gray-400">({item.count})</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="tag-search-assemble-item flex items-center justify-end text-xs text-gray-500 dark:text-gray-400 ivory:text-coffee-500" style={{ '--assemble-delay': '420ms' } as React.CSSProperties}>
                                    {!isSearching && wordTotalPages > 1 && (
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setWordPage((prev) => Math.max(1, prev - 1))}
                                                disabled={wordPage <= 1}
                                                className="px-2 py-1 rounded-md border border-gray-200 dark:border-gray-700 ivory:border-coffee-200 text-gray-600 dark:text-gray-200 ivory:text-coffee-700 disabled:text-gray-400 ivory:disabled:text-coffee-400 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-800 ivory:hover:bg-coffee-200 transition-colors"
                                                onMouseEnter={(e) => showTooltip(content.tooltips[language].pagination.prev, e)}
                                                onMouseLeave={hideTooltip}
                                            >
                                                {content.ui[language].pagination.previous}
                                            </button>
                                            <span>{wordPage} / {wordTotalPages}</span>
                                            <button
                                                type="button"
                                                onClick={() => setWordPage((prev) => Math.min(wordTotalPages, prev + 1))}
                                                disabled={wordPage >= wordTotalPages}
                                                className="px-2 py-1 rounded-md border border-gray-200 dark:border-gray-700 ivory:border-coffee-200 text-gray-600 dark:text-gray-200 ivory:text-coffee-700 disabled:text-gray-400 ivory:disabled:text-coffee-400 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-800 ivory:hover:bg-coffee-200 transition-colors"
                                                onMouseEnter={(e) => showTooltip(content.tooltips[language].pagination.next, e)}
                                                onMouseLeave={hideTooltip}
                                            >
                                                {content.ui[language].pagination.next}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="tag-search-assemble rounded-3xl border border-gray-200 dark:border-gray-700 ivory:border-coffee-200 bg-white/90 dark:bg-gray-900/70 ivory:bg-coffee-50 p-3 shadow-sm">
                                <div
                                    className="tag-search-assemble-item text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 ivory:text-coffee-500 mb-3"
                                    onMouseEnter={(e) => showTooltip(content.tooltips[language].tagSearch.recent, e)}
                                    onMouseLeave={hideTooltip}
                                    style={{ '--assemble-delay': '0ms' } as React.CSSProperties}
                                >
                                    {content.ui[language].tagSearch.recentTitle}
                                </div>
                                <div className="tag-search-assemble-item flex flex-wrap gap-1.5" style={{ '--assemble-delay': '140ms' } as React.CSSProperties}>
                                    {recentTags.length === 0 && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400 ivory:text-coffee-500">{content.ui[language].tagSearch.noSuggestions}</span>
                                    )}
                                    {recentTags.map(tag => (
                                        <button
                                            key={tag}
                                            type="button"
                                            aria-pressed={(draft.get('tags') || '').split(',').includes(tag)}
                                            onClick={event => handleSelectWord(tag, event.currentTarget)}
                                            className="px-3 py-1.5 rounded-full bg-blue-50 dark:bg-blue-900/40 ivory:bg-coffee-200/80 text-sm font-semibold text-blue-700 dark:text-blue-200 ivory:text-coffee-800"
                                        >
                                            {tag}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="tag-search-assemble rounded-3xl border border-gray-200 dark:border-gray-700 ivory:border-coffee-200 bg-white/90 dark:bg-gray-900/70 ivory:bg-coffee-50 p-3 shadow-sm">
                                <div
                                    className="tag-search-assemble-item text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 ivory:text-coffee-500 mb-3"
                                    onMouseEnter={(e) => showTooltip(content.tooltips[language].tagSearch.frequent, e)}
                                    onMouseLeave={hideTooltip}
                                    style={{ '--assemble-delay': '0ms' } as React.CSSProperties}
                                >
                                    {content.ui[language].tagSearch.frequentTitle}
                                </div>
                                <div className="tag-search-assemble-item flex flex-wrap gap-1.5" style={{ '--assemble-delay': '140ms' } as React.CSSProperties}>
                                    {frequentTags.length === 0 && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400 ivory:text-coffee-500">{content.ui[language].tagSearch.noSuggestions}</span>
                                    )}
                                    {frequentTags.map(tag => (
                                        <button
                                            key={tag}
                                            type="button"
                                            aria-pressed={(draft.get('tags') || '').split(',').includes(tag)}
                                            onClick={event => handleSelectWord(tag, event.currentTarget)}
                                            className="px-3 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-900/40 ivory:bg-coffee-200/80 text-sm font-semibold text-emerald-700 dark:text-emerald-200 ivory:text-coffee-800"
                                        >
                                            {tag}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="tag-search-assemble rounded-3xl border border-gray-200 dark:border-gray-700 ivory:border-coffee-200 bg-white/90 dark:bg-gray-900/70 ivory:bg-coffee-50 p-3 shadow-sm">
                                <div
                                    className="tag-search-assemble-item text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 ivory:text-coffee-500 mb-3"
                                    onMouseEnter={(e) => showTooltip(content.tooltips[language].tagSearch.popular, e)}
                                    onMouseLeave={hideTooltip}
                                    style={{ '--assemble-delay': '0ms' } as React.CSSProperties}
                                >
                                    {content.ui[language].tagSearch.popularTitle}
                                </div>
                                <div className="tag-search-assemble-item flex flex-wrap gap-1.5" style={{ '--assemble-delay': '140ms' } as React.CSSProperties}>
                                    {loadingPopular && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400 ivory:text-coffee-500">{content.ui[language].tagSearch.loading}</span>
                                    )}
                                    {!loadingPopular && popularTags.length === 0 && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400 ivory:text-coffee-500">{content.ui[language].tagSearch.noSuggestions}</span>
                                    )}
                                    {popularTags.map(tag => (
                                        <button
                                            key={tag.word}
                                            type="button"
                                            aria-pressed={(draft.get('tags') || '').split(',').includes(tag.word)}
                                            onClick={event => handleSelectWord(tag.word, event.currentTarget)}
                                            className="px-3 py-1.5 rounded-full bg-amber-50 dark:bg-amber-900/40 ivory:bg-coffee-200/80 text-sm font-semibold text-amber-700 dark:text-amber-200 ivory:text-coffee-800"
                                        >
                                            {tag.word}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="tag-search-assemble rounded-3xl border border-gray-200 dark:border-gray-700 ivory:border-coffee-200 bg-white/90 dark:bg-gray-900/70 ivory:bg-coffee-50 p-3 shadow-sm">
                                <div
                                    className="tag-search-assemble-item text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 ivory:text-coffee-500 mb-3"
                                    onMouseEnter={(e) => showTooltip(content.tooltips[language].tagSearch.related, e)}
                                    onMouseLeave={hideTooltip}
                                    style={{ '--assemble-delay': '0ms' } as React.CSSProperties}
                                >
                                    {content.ui[language].tagSearch.relatedTitle}
                                </div>
                                <div className="tag-search-assemble-item flex flex-wrap gap-1.5" style={{ '--assemble-delay': '140ms' } as React.CSSProperties}>
                                    {(!isImageView || browsableRelatedTags.length === 0) && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400 ivory:text-coffee-500">{content.ui[language].tagSearch.noSuggestions}</span>
                                    )}
                                    {isImageView && browsableRelatedTags.map(tag => (
                                        <button
                                            key={tag}
                                            type="button"
                                            aria-pressed={(draft.get('tags') || '').split(',').includes(tag)}
                                            onClick={event => handleSelectWord(tag, event.currentTarget)}
                                            className="px-3 py-1.5 rounded-full bg-teal-50 dark:bg-teal-900/40 ivory:bg-coffee-200/80 text-sm font-semibold text-teal-700 dark:text-teal-200 ivory:text-coffee-800"
                                        >
                                            {tag}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                </>}
                </div>
                <div className="lab-search-footer flex flex-wrap items-center justify-between gap-2">
                    <button
                        type="button"
                        onClick={handleClear}
                        className="tag-search-assemble-item px-3 py-1.5 rounded-full bg-gray-200 dark:bg-gray-700 ivory:bg-coffee-200 text-gray-700 dark:text-gray-200 ivory:text-coffee-800 text-sm font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 ivory:hover:bg-coffee-300 transition-colors"
                        style={{ '--assemble-delay': '140ms' } as React.CSSProperties}
                        onMouseEnter={(e) => showTooltip(content.tooltips[language].tagSearch.clear, e)}
                        onMouseLeave={hideTooltip}
                    >
                        {content.ui[language].tagSearch.clear}
                    </button>
                    <button type="button" className="lab-button" onClick={closeTagSearch}>{t('search.cancel')}</button>
                    {draftQuery && <>
                        {currentCountStatus === 'error' && <div className="lab-search-count-error"><span role="alert">{t('search.error')}</span> <button type="button" className="lab-button" onClick={() => setCountAttempt(value => value + 1)}>{t('common.retry')}</button></div>}
                        <button type="button" className="lab-button lab-search-apply" disabled={currentCountStatus !== 'ready'} onClick={handleApply}>
                            {t('search.apply')} <span role="status">({currentCountStatus === 'ready' && count !== null ? t('search.count', { count }) : currentCountStatus === 'error' ? t('search.countError') : t('search.countLoading')})</span>
                        </button>
                    </>}
                </div>
            </div>
        </Modal>
    );
};

export default TagSearchModal;
